import { useState } from 'react';
import { Box, Card, Divider, Group, Select, Stack, Switch, Text, Title } from '@/components/ui';
import {
  IconBell,
  IconBellOff,
  IconVolume,
  IconVolumeOff,
  IconMoon,
  IconSun,
  IconLock,
  IconEye,
  IconEyeOff,
} from '@tabler/icons-react';

export default function SettingsPage() {
  const [notifications, setNotifications] = useState(true);
  const [sounds, setSounds] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [showOnlineStatus, setShowOnlineStatus] = useState(true);
  const [readReceipts, setReadReceipts] = useState(true);
  const [typingIndicators, setTypingIndicators] = useState(true);
  const [messagePreview, setMessagePreview] = useState<string | null>('full');
  const [autoDownload, setAutoDownload] = useState<string | null>('wifi');

  return (
    <Stack gap="lg">
      {/* Header */}
      <Box>
        <Title order={2}>Chat Settings</Title>
        <Text c="dimmed" size="sm">Customize your chat experience</Text>
      </Box>

      {/* Notifications Settings */}
      <Card>
        <Text fw={600} mb="md">Notifications</Text>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="sm">
              {notifications ? <IconBell size={20} /> : <IconBellOff size={20} />}
              <Box>
                <Text size="sm">Push Notifications</Text>
                <Text size="xs" c="dimmed">Receive notifications for new messages</Text>
              </Box>
            </Group>
            <Switch
              checked={notifications}
              onChange={(e) => setNotifications(e.currentTarget.checked)}
            />
          </Group>

          <Group justify="space-between">
            <Group gap="sm">
              {sounds ? <IconVolume size={20} /> : <IconVolumeOff size={20} />}
              <Box>
                <Text size="sm">Message Sounds</Text>
                <Text size="xs" c="dimmed">Play sound for incoming messages</Text>
              </Box>
            </Group>
            <Switch
              checked={sounds}
              onChange={(e) => setSounds(e.currentTarget.checked)}
            />
          </Group>

          <Group justify="space-between">
            <Group gap="sm">
              <IconEye size={20} />
              <Box>
                <Text size="sm">Message Preview</Text>
                <Text size="xs" c="dimmed">Show message content in notifications</Text>
              </Box>
            </Group>
            <Select
              value={messagePreview}
              onChange={setMessagePreview}
              data={[
                { value: 'full', label: 'Full message' },
                { value: 'sender', label: 'Sender only' },
                { value: 'none', label: 'No preview' },
              ]}
              w={150}
            />
          </Group>
        </Stack>
      </Card>

      {/* Privacy Settings */}
      <Card>
        <Text fw={600} mb="md">Privacy</Text>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="sm">
              {showOnlineStatus ? <IconEye size={20} /> : <IconEyeOff size={20} />}
              <Box>
                <Text size="sm">Online Status</Text>
                <Text size="xs" c="dimmed">Show when you're online to others</Text>
              </Box>
            </Group>
            <Switch
              checked={showOnlineStatus}
              onChange={(e) => setShowOnlineStatus(e.currentTarget.checked)}
            />
          </Group>

          <Group justify="space-between">
            <Group gap="sm">
              <IconLock size={20} />
              <Box>
                <Text size="sm">Read Receipts</Text>
                <Text size="xs" c="dimmed">Let others know when you've read messages</Text>
              </Box>
            </Group>
            <Switch
              checked={readReceipts}
              onChange={(e) => setReadReceipts(e.currentTarget.checked)}
            />
          </Group>

          <Group justify="space-between">
            <Group gap="sm">
              <IconLock size={20} />
              <Box>
                <Text size="sm">Typing Indicators</Text>
                <Text size="xs" c="dimmed">Show when you're typing a message</Text>
              </Box>
            </Group>
            <Switch
              checked={typingIndicators}
              onChange={(e) => setTypingIndicators(e.currentTarget.checked)}
            />
          </Group>
        </Stack>
      </Card>

      {/* Appearance Settings */}
      <Card>
        <Text fw={600} mb="md">Appearance</Text>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="sm">
              {darkMode ? <IconMoon size={20} /> : <IconSun size={20} />}
              <Box>
                <Text size="sm">Dark Mode</Text>
                <Text size="xs" c="dimmed">Use dark theme for chat</Text>
              </Box>
            </Group>
            <Switch
              checked={darkMode}
              onChange={(e) => setDarkMode(e.currentTarget.checked)}
            />
          </Group>
        </Stack>
      </Card>

      {/* Data & Storage */}
      <Card>
        <Text fw={600} mb="md">Data & Storage</Text>
        <Stack gap="md">
          <Group justify="space-between">
            <Box>
              <Text size="sm">Auto-download Media</Text>
              <Text size="xs" c="dimmed">Automatically download images and files</Text>
            </Box>
            <Select
              value={autoDownload}
              onChange={setAutoDownload}
              data={[
                { value: 'always', label: 'Always' },
                { value: 'wifi', label: 'Wi-Fi only' },
                { value: 'never', label: 'Never' },
              ]}
              w={150}
            />
          </Group>

          <Divider />

          <Group justify="space-between">
            <Box>
              <Text size="sm">Storage Used</Text>
              <Text size="xs" c="dimmed">Cached messages and media</Text>
            </Box>
            <Text size="sm" fw={500}>245 MB</Text>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
